import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistAdmin() {
  const [params] = useSearchParams();
  const key = params.get("key") || "";
  const [state, setState] = useState({ loading: true, error: "", entries: [], count: 0 });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/waitlist?key=${encodeURIComponent(key)}`);
        setState({ loading: false, error: "", entries: res.data.entries, count: res.data.count });
      } catch (err) {
        setState({
          loading: false,
          error: err?.response?.status === 401 ? "Unauthorized — add ?key=<admin-key> to the URL." : "Could not load waitlist.",
          entries: [],
          count: 0,
        });
      }
    })();
  }, [key]);

  const downloadCsv = () => {
    const headers = ["created_at", "category", "name", "email", "phone", "child_age", "insight", "camera_status", "question"];
    const rows = state.entries.map((e) =>
      headers.map((h) => `"${(e[h] || "").toString().replace(/"/g, '""')}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aavaa-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const categoryBadge = (v) => {
    const cat = (v || "childcare").toLowerCase();
    const map = {
      childcare: { label: "Child-care", cls: "bg-[#8E9B82]/15 text-[#7A8A6E] border-[#8E9B82]/30" },
      eldercare: { label: "Elder care", cls: "bg-[#C59D5D]/15 text-[#A37A40] border-[#C59D5D]/40" },
    };
    const m = map[cat] || { label: cat, cls: "bg-[#F7F5F0] text-[#6B7264] border-[#E2DFD8]" };
    return (
      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${m.cls}`}>
        {m.label}
      </span>
    );
  };

  const cameraBadge = (v) => {
    if (!v) return "—";
    const map = { yes: "Yes", no: "No", planning: "Planning" };
    const classes = {
      yes: "bg-[#8E9B82]/15 text-[#7A8A6E]",
      no: "bg-[#D4A373]/15 text-[#B8864F]",
      planning: "bg-[#E2DFD8] text-[#6B7264]",
    };
    return (
      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium ${classes[v] || "bg-[#F7F5F0] text-[#6B7264]"}`}>
        {map[v] || v}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#2F3630] p-6 sm:p-10" data-testid="waitlist-admin-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="font-manrope text-xs tracking-[0.15em] text-[#D4A373] uppercase mb-1">Admin</p>
            <h1 className="font-outfit text-3xl font-medium tracking-tight">Aavaa demo requests</h1>
            {!state.loading && !state.error && (
              <p className="font-manrope text-sm text-[#6B7264] mt-1" data-testid="waitlist-count">
                {state.count} {state.count === 1 ? "person has" : "people have"} requested a demo.
              </p>
            )}
          </div>
          {state.entries.length > 0 && (
            <button
              data-testid="waitlist-download-csv"
              onClick={downloadCsv}
              className="flex items-center gap-2 rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-10 px-5 font-outfit text-sm transition-colors"
            >
              <Download size={14} /> Download CSV
            </button>
          )}
        </div>

        {state.loading && <p className="font-manrope text-[#6B7264]">Loading...</p>}
        {state.error && (
          <div className="bg-white rounded-2xl border border-[#E2DFD8] p-6 font-manrope text-sm text-[#6B7264]" data-testid="waitlist-error">
            {state.error}
          </div>
        )}

        {!state.loading && !state.error && state.entries.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#E2DFD8] p-8 text-center font-manrope text-sm text-[#6B7264]">
            No demo requests yet.
          </div>
        )}

        {state.entries.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-manrope">
                <thead className="bg-[#F7F5F0] text-[#6B7264] text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3 whitespace-nowrap">When</th>
                    <th className="text-left px-4 py-3">Category</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Contact</th>
                    <th className="text-left px-4 py-3">Child age</th>
                    <th className="text-left px-4 py-3">Has camera?</th>
                    <th className="text-left px-4 py-3 min-w-[280px]">What insight they're after</th>
                    <th className="text-left px-4 py-3 min-w-[220px]">Their question</th>
                  </tr>
                </thead>
                <tbody>
                  {state.entries.map((e) => (
                    <tr key={e.id} className="border-t border-[#E2DFD8]/60 align-top hover:bg-[#FAFAF7]">
                      <td className="px-4 py-3 text-[#6B7264] whitespace-nowrap text-xs">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">{categoryBadge(e.category)}</td>
                      <td className="px-4 py-3 text-[#2F3630] font-medium">{e.name || "—"}</td>
                      <td className="px-4 py-3 text-[#2F3630]">
                        {e.email && <div>{e.email}</div>}
                        {e.phone && <div className="text-xs text-[#6B7264]">{e.phone}</div>}
                        {!e.email && !e.phone && "—"}
                      </td>
                      <td className="px-4 py-3 text-[#6B7264]">{e.child_age || "—"}</td>
                      <td className="px-4 py-3">{cameraBadge(e.camera_status)}</td>
                      <td className="px-4 py-3 text-[#2F3630] text-sm leading-relaxed">{e.insight || "—"}</td>
                      <td className="px-4 py-3 text-[#6B7264] text-sm leading-relaxed">{e.question || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
